import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NavigationService } from '@ngx-seatback/utils';
import { SbLineChartModel } from '@ngx-seatback/charts';
import {
  API,
  BookingDTO,
  BookingStatus,
  CampaignWithPreviewDTO,
  CommercialWithPreviewDTO,
  CompanyDTO,
  CullDTO,
  CullWithAtomsDTO,
  GeoCullDTO,
  InventoryGeoAggregationFrontendEnhancedDTO,
  MediumGroupFullDTO,
  PlayerCullDTO,
  RequirementDTO,
  RequirementFullDTO,
  StateSpaceCullDTO,
  TimeCullDTO,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { ClusterDefinition, MinMaxPoints } from '../../../../services/global/global-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { iif, of } from 'rxjs';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';
import { QueryParamService } from '../../../../services/lists/query-params';

@Component({
  selector: 'sb-page-requirement-edit',
  templateUrl: './requirement-edit.component.html',
  styleUrls: ['./requirement-edit.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [style({ transform: 'translateX(+100%)' }), animate('200ms', style({ transform: 'translateX(0%)' }))]),
      transition(':leave', [animate('200ms', style({ transform: 'translateX(+100%)' }))]),
    ]),
  ],
  standalone: false,
})
export class RequirementEditComponent implements OnInit {
  @ViewChild('wizard') wizard: SbWizardComponent;
  @ViewChild('tab3') tab3: SbWizardTabComponent;

  form: UntypedFormGroup;

  clientId: number;
  campaignId: number;
  selectedCompany: CompanyDTO;
  campaign: CampaignWithPreviewDTO;
  lineChartGradient: SbLineChartModel;
  commercial: CommercialWithPreviewDTO;
  requirement: RequirementFullDTO;
  useMotiveGroupForBookings: boolean;
  selectedMotiveGroup: MediumGroupFullDTO;
  bookings: BookingDTO[];
  commercialId: number;
  requirementId: number;
  previewRequirementData: RequirementDTO;
  loading: boolean;
  wizardState: string;
  wizardPreviewState: string;
  minMaxPoints: MinMaxPoints;
  clusters: ClusterDefinition[];
  createRequirementClick: boolean;
  pickedPosition: RequirementDTO;
  requirements: RequirementDTO[];

  geoAggregation: InventoryGeoAggregationFrontendEnhancedDTO;
  private hasRequirementDataChanged: boolean;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly api: API,
    public readonly modal: ModalService,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly navService: NavigationService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef,
    private readonly queryParamService: QueryParamService
  ) {
    this.clientId = undefined;
    this.campaignId = undefined;
    this.campaign = null;
    this.commercial = null;
    this.requirement = null;
    this.useMotiveGroupForBookings = false;
    this.selectedMotiveGroup = undefined;
    this.bookings = null;
    this.commercialId = undefined;
    this.requirementId = undefined;
    this.previewRequirementData = undefined;
    this.loading = true;
    this.wizardState = 'Map';
    this.wizardPreviewState = 'locationMap';
    this.minMaxPoints = undefined;
    this.clusters = undefined;
    this.createRequirementClick = false;
    this.form = new UntypedFormGroup({
      Title: new UntypedFormControl('', [Validators.required]),
      Identifier: new UntypedFormControl(''),
      Description: new UntypedFormControl(''),
    });
    this.hasRequirementDataChanged = false;

    this.lineChartGradient = {
      title: 'Test',
      height: 150,
      value: 'value',
      category: 'day',
      fill: true,
      gradient: true,
      data: [
        {
          day: '1',
          value: 60,
        },
        {
          day: '2',
          value: 70,
        },
        {
          day: '3',
          value: 30,
        },
        {
          day: '4',
          value: 90,
        },
        {
          day: '5',
          value: 64,
        },
        {
          day: '6',
          value: 72,
        },
        {
          day: '7',
          value: 82,
        },
      ],
    };
    this.selectedCompany = null;
  }

  get Title() {
    return this.form.get('Title');
  }

  get Description() {
    return this.form.get('Description');
  }

  get Identifier() {
    return this.form.get('Identifier');
  }

  nextStep(wizard: any, nextTab: any) {
    wizard.selectTab(nextTab);
  }

  sliceCull(e: MouseEvent, type: string) {
    switch (type) {
      case 'TimeCull': {
        if (this.requirement.timeCull) {
          this.requirement.timeCull = null;
        }
        break;
      }
      case 'PlayerCull': {
        if (this.requirement.playerCull) {
          this.requirement.playerCull = null;
          this.refreshMapSettings();
        }
        break;
      }
      case 'GeoCull': {
        if (this.requirement.geoCull) {
          this.requirement.geoCull = null;
          this.refreshMapSettings();
        }
        break;
      }
    }
    this.previewRequirement();
  }

  selectFilter(cull: CullDTO) {
    this.setCull(cull);
    this.changeWizardState('Map');
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  changeWizardPreviewState(state: string) {
    this.wizardPreviewState = state;
  }

  filterSubmitSuccess(cull: CullWithAtomsDTO) {
    if (!cull) {
      this.changeWizardState('Map');
    } else {
      switch (cull.type) {
        case 'TimeCull':
          this.selectFilter(cull as TimeCullDTO);
          break;
        case 'PlayerCull':
          this.selectFilter(cull as PlayerCullDTO);
          break;
      }
    }
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      this.clientId = params.clientID;
      this.campaignId = params.campaignID;
      this.commercialId = params.commercialID;
      this.requirementId = params.requirementID;
      this.api
        .ds_cm_Commercial_retrieveRequirements(this.commercialId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (requirementData: RequirementFullDTO[]) => {
            this.requirements = requirementData;
            this.pickedPosition = this.requirementId ? requirementData.filter(x => x.id === this.requirementId)[0] : null;
            this.refreshFromServer();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      this.requirement.name = value;
    });
    this.Identifier.valueChanges.subscribe(value => {
      this.requirement.identifier = value;
    });
    this.Description.valueChanges.subscribe(value => {
      this.requirement.description = value;
    });
  }

  updateForm() {
    this.Title.setValue(this.requirement.name);
    this.Identifier.setValue(this.requirement.identifier);
    this.Description.setValue(this.requirement.description);
  }

  refreshFromServer() {
    this.loading = true;
    iif(() => !this.requirementId, of(null), this.api.ds_cm_Requirement_retrieveById(this.requirementId))
      .pipe(
        switchMap((requirementData: RequirementFullDTO) => {
          if (!requirementData) {
            this.requirement = new RequirementFullDTO();
            this.requirement.name = '';
            this.requirement.active = true;
          } else {
            this.requirement = requirementData;
            this.bookings = requirementData.bookings;
            this.updateForm();
          }
          return this.api.ds_cm_Campaign_retrieveByIdFrontendEnhanced(this.campaignId);
        }),
        switchMap((campaignData: CampaignWithPreviewDTO) => {
          this.campaign = campaignData;
          return this.api.ds_cm_Commercial_retrieveByIdFrontendEnhanced(this.commercialId);
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (commercialData: CommercialWithPreviewDTO) => {
          this.commercial = commercialData;
          this.previewRequirement();
          if (this.queryParamService.getQueryParam('skipToBooking')) {
            this.wizard.selectTab(this.tab3);
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  navigateBackToRequirementsList() {
    this.modal.close('finishmodal');
    this.navService.navigateToPoint(RoutingID.requirements, [
      {
        id: 'campaignID',
        value: this.campaignId,
      },
      { id: 'commercialID', value: this.commercialId },
    ]);
  }

  createAndUpdateRequirement(completeAction: any, wizard: any, nextTab: any) {
    this.createRequirementClick = true;
    this.requirement.priority = this.pickedPosition ? this.pickedPosition.priority : 0;
    if (!this.requirement?.id) {
      this.api
        .ds_cm_Commercial_createRequirement(this.commercial.id, this.requirement)
        .pipe(
          take(1),
          finalize(() => {
            this.createRequirementClick = false;
          })
        )
        .subscribe({
          next: (requirementData: RequirementFullDTO) => {
            this.requirement = requirementData;
            wizard.selectTab(nextTab);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ds_cm_Requirement_editRequirement(this.requirement)
        .pipe(
          take(1),
          finalize(() => {
            this.createRequirementClick = false;
          })
        )
        .subscribe({
          next: (requirementData: RequirementFullDTO) => {
            this.requirement = requirementData;
            wizard.selectTab(nextTab);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  setCull(cull: CullDTO) {
    switch (cull.type) {
      case 'TimeCull':
        this.requirement.timeCull = cull as TimeCullDTO;
        if (!cull.id) {
          this.requirement.timeCull = null;
        }
        break;
      case 'StateSpaceCull':
        this.requirement.stateSpaceCull = cull as StateSpaceCullDTO;
        if (!cull.id) {
          this.requirement.stateSpaceCull = null;
        }
        break;
      case 'GeoCull':
        this.requirement.geoCull = cull as GeoCullDTO;
        if (!cull.id) {
          this.requirement.geoCull = null;
        }
        break;
      case 'PlayerCull':
        this.requirement.playerCull = cull as PlayerCullDTO;
        if (!cull.id) {
          this.requirement.playerCull = null;
        }
        break;
    }
    this.previewRequirement();
  }

  previewRequirement() {
    if (!this.requirementId && !this.requirement.id) {
      this.api
        .ds_cm_Commercial_createRequirementPreview(this.commercial ? this.commercial.id : null, this.requirement)
        .pipe(take(1))
        .subscribe({
          next: data => {
            this.previewRequirementData = data;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ds_cm_Requirement_editRequirementPreview(this.requirement)
        .pipe(take(1))
        .subscribe({
          next: data => {
            this.previewRequirementData = data;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
    this.refreshMap();
  }

  finish() {
    this.loading = true;
    iif(() => this.hasRequirementDataChanged, this.api.ds_cm_Requirement_editRequirement(this.requirement), of({}))
      .pipe(
        switchMap(() => this.api.ds_cm_Booking_editBookings(this.requirement.bookings)),
        take(1)
      )
      .subscribe({
        next: () => {
          this.showFinishModal();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  private showFinishModal() {
    this.modal
      .modal(
        {
          title: !this.commercialId
            ? this.translate.instant('lang.requirementEdit.createdSuccessfully')
            : this.translate.instant('lang.requirementEdit.editedSuccessfully'),
          modalOptions: { windowClass: 'M' },
        },
        {
          templateId: 'finishmodal',
        }
      )
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        this.navigateBackToRequirementsList();
      });
  }

  updateBookings(bookings: BookingDTO[]) {
    this.requirement.bookings = bookings;
  }

  showBookingsNotice(): boolean {
    for (const booking of this.requirement.bookings) {
      if (booking.status === BookingStatus.VerificationRequested) {
        return true;
      }
    }
    return false;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  refreshMap(point?: MinMaxPoints) {
    if (point) {
      this.minMaxPoints = point;
    }
    this.refreshMapSettings();
  }

  refreshMapSettings() {
    this.api
      .ds_rm_Inventory_geoAggregationFrontendEnhanced(
        99,
        {
          filters: null,
          whitelistPolygons: null,
          blacklistPolygons: null,
          playerCulls: this.getPlayerCullArray(this.campaign?.playerCull, this.commercial?.playerCull, this.requirement?.playerCull),
          geoCulls: this.getGeoCullArray(this.campaign?.geoCull, this.commercial?.geoCull, this.requirement?.geoCull),
          descriptorId: null,
        },
        { min: this.minMaxPoints?.min, max: this.minMaxPoints?.max }
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (aggregation: InventoryGeoAggregationFrontendEnhancedDTO) => {
          this.clusters = aggregation.data;
          this.geoAggregation = aggregation;
        },
      });
  }

  getPlayerCullArray(campaignCull: PlayerCullDTO, commercialCull: PlayerCullDTO, requirementCull: PlayerCullDTO): PlayerCullDTO[] | null {
    const res: PlayerCullDTO[] = [];
    if (!campaignCull && !commercialCull && !requirementCull) {
      return null;
    }
    if (campaignCull) {
      res.push(campaignCull);
    }
    if (commercialCull) {
      res.push(commercialCull);
    }
    if (requirementCull) {
      res.push(requirementCull);
    }
    return res;
  }

  getGeoCullArray(campaignCull: GeoCullDTO, commercialCull: GeoCullDTO, requirementCull: GeoCullDTO): GeoCullDTO[] | null {
    const res: GeoCullDTO[] = [];
    if (!campaignCull && !commercialCull && !requirementCull) {
      return null;
    }
    if (campaignCull) {
      res.push(campaignCull);
    }
    if (commercialCull) {
      res.push(commercialCull);
    }
    if (requirementCull) {
      res.push(requirementCull);
    }
    return res;
  }

  openInventoryView() {
    this.modal
      .modal(
        {
          title: '',
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'inventorySelectionModal',
        }
      )
      .then((cull: PlayerCullDTO) => {
        if (cull) {
          this.setCull(cull);
        }
      })
      .catch(() => {});
  }

  updatePosition($event: RequirementDTO) {
    this.pickedPosition = $event;
  }

  requirementDataChanged($event: boolean) {
    this.hasRequirementDataChanged = $event;
  }
}
